import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHistory,
  faUser,
  faLayerGroup,
  faSignInAlt,
  faSignOutAlt,
  faUserPlus,
  faTrash,
  faEdit,
  faShoppingCart,
  faBox,
  faCog,
  faSearch,
  faFilter,
  faCalendar,
  faSync,
  faChevronLeft,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import api from '../../services/api';
import './Activities.css';

const Activities = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 10;

  // Activity type mapping for icons and colors
  const activityTypes = {
    login: { icon: faSignInAlt, color: '#10b981', label: 'Connexion' },
    logout: { icon: faSignOutAlt, color: '#6b7280', label: 'Déconnexion' },
    register: { icon: faUserPlus, color: '#3b82f6', label: 'Inscription' },
    user_update: { icon: faEdit, color: '#f59e0b', label: 'Modification utilisateur' },
    user_delete: { icon: faTrash, color: '#ef4444', label: 'Suppression utilisateur' },
    group_create: { icon: faLayerGroup, color: '#8b5cf6', label: 'Création groupe' },
    group_update: { icon: faEdit, color: '#f59e0b', label: 'Modification groupe' },
    group_delete: { icon: faTrash, color: '#ef4444', label: 'Suppression groupe' },
    group_join: { icon: faLayerGroup, color: '#10b981', label: 'Rejoindre groupe' },
    group_leave: { icon: faLayerGroup, color: '#6b7280', label: 'Quitter groupe' },
    product_create: { icon: faBox, color: '#3b82f6', label: 'Création produit' },
    product_update: { icon: faEdit, color: '#f59e0b', label: 'Modification produit' },
    product_delete: { icon: faTrash, color: '#ef4444', label: 'Suppression produit' },
    order_create: { icon: faShoppingCart, color: '#10b981', label: 'Nouvelle commande' },
    order_update: { icon: faEdit, color: '#f59e0b', label: 'Modification commande' },
    settings_update: { icon: faCog, color: '#6b7280', label: 'Modification paramètres' },
    admin_action: { icon: faUser, color: '#ec4899', label: 'Action admin' },
    create_admin: { icon: faUserPlus, color: '#10b981', label: 'Création admin' },
    delete_admin: { icon: faTrash, color: '#ef4444', label: 'Suppression admin' },
    update_permissions: { icon: faEdit, color: '#f59e0b', label: 'Modification permissions' },
    promote_super_admin: { icon: faUser, color: '#8b5cf6', label: 'Promotion super admin' },
    demote_super_admin: { icon: faUser, color: '#f59e0b', label: 'Rétrogradation admin' },
    change_password: { icon: faCog, color: '#6b7280', label: 'Changement mot de passe' },
    reset_password: { icon: faCog, color: '#f59e0b', label: 'Réinitialisation mot de passe' },
    recover_group: { icon: faLayerGroup, color: '#10b981', label: 'Récupération groupe' },
    recover_user: { icon: faUserPlus, color: '#10b981', label: 'Récupération utilisateur' },
    delete_group: { icon: faTrash, color: '#ef4444', label: 'Suppression groupe' },
    delete_user: { icon: faTrash, color: '#ef4444', label: 'Suppression utilisateur' },
    grant_gestion_access: { icon: faLayerGroup, color: '#10b981', label: 'Octroi accès Gestion' },
    revoke_gestion_access: { icon: faLayerGroup, color: '#ef4444', label: 'Retrait accès Gestion' },
    approve_deletion: { icon: faTrash, color: '#10b981', label: 'Approbation suppression' },
    reject_deletion: { icon: faTrash, color: '#6b7280', label: 'Refus suppression' },
    approve_gestion: { icon: faLayerGroup, color: '#10b981', label: 'Approbation Gestion' },
    reject_gestion: { icon: faLayerGroup, color: '#6b7280', label: 'Refus Gestion' },
    deactivate_admin: { icon: faUser, color: '#ef4444', label: 'Désactivation admin' },
    reactivate_admin: { icon: faUser, color: '#10b981', label: 'Réactivation admin' },
    request_gestion_change: { icon: faLayerGroup, color: '#f59e0b', label: 'Demande changement Gestion' },
    approve_gestion_request: { icon: faLayerGroup, color: '#10b981', label: 'Approbation demande Gestion' },
    reject_gestion_request: { icon: faLayerGroup, color: '#ef4444', label: 'Refus demande Gestion' },
    default: { icon: faHistory, color: '#6b7280', label: 'Activité' }
  };

  // Helper function to translate action names
  const translateAction = (action) => {
    const translations = {
      'login': 'Connexion',
      'logout': 'Déconnexion',
      'register': 'Inscription',
      'user_update': 'Modification utilisateur',
      'user_delete': 'Suppression utilisateur',
      'group_create': 'Création groupe',
      'group_update': 'Modification groupe',
      'group_delete': 'Suppression groupe',
      'group_join': 'Rejoindre groupe',
      'group_leave': 'Quitter groupe',
      'product_create': 'Création produit',
      'product_update': 'Modification produit',
      'product_delete': 'Suppression produit',
      'order_create': 'Nouvelle commande',
      'order_update': 'Modification commande',
      'settings_update': 'Modification paramètres',
      'admin_action': 'Action admin',
      'create_admin': 'Création admin',
      'delete_admin': 'Suppression admin',
      'update_permissions': 'Modification permissions',
      'promote_super_admin': 'Promotion super admin',
      'demote_super_admin': 'Rétrogradation admin',
      'change_password': 'Changement mot de passe',
      'reset_password': 'Réinitialisation mot de passe',
      'recover_group': 'Récupération groupe',
      'recover_user': 'Récupération utilisateur',
      'delete_group': 'Suppression groupe',
      'delete_user': 'Suppression utilisateur',
      'grant_gestion_access': 'Octroi accès Gestion',
      'revoke_gestion_access': 'Retrait accès Gestion',
      'approve_deletion': 'Approbation suppression',
      'reject_deletion': 'Refus suppression',
      'approve_gestion': 'Approbation Gestion',
      'reject_gestion': 'Refus Gestion',
      'deactivate_admin': 'Désactivation admin',
      'reactivate_admin': 'Réactivation admin',
      'request_gestion_change': 'Demande changement Gestion',
      'approve_gestion_request': 'Approbation demande Gestion',
      'reject_gestion_request': 'Refus demande Gestion'
    };
    return translations[action] || action.replace(/_/g, ' ');
  };

  const fetchActivities = useCallback(async () => {
    setLoading(true);
    try {
      // Try to get from admin activities endpoint
      const params = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage,
        ...(filterType !== 'all' && { type: filterType }),
        ...(searchTerm && { search: searchTerm }),
        ...(dateRange.start && { startDate: dateRange.start }),
        ...(dateRange.end && { endDate: dateRange.end })
      });
      
      // First try the audit logs from dashboard admin
      const auditResponse = await api.get('/dashboard-admin/audit?limit=100');
      const auditData = auditResponse.data || [];
      
      const mappedActivities = auditData.map(a => ({
        id: a.id,
        type: a.action,
        description: a.target_name 
          ? `${translateAction(a.action)} - ${a.target_name}`
          : translateAction(a.action),
        user: a.admin_name || a.admin_email,
        email: a.admin_email,
        target: a.target_name,
        created_at: a.created_at,
        ip_address: a.ip_address,
        details: a.details
      }));
      
      setActivities(mappedActivities);
      setTotalCount(mappedActivities.length);
    } catch (error) {
      console.error('Error fetching activities:', error);
      setActivities([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, filterType, searchTerm, dateRange]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const getActivityInfo = (type) => {
    return activityTypes[type] || activityTypes.default;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays} jour${diffDays > 1 ? 's' : ''}`;
    
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter activities based on search and filter
  const filteredActivities = activities.filter(activity => {
    const matchesSearch = !searchTerm || 
      activity.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activity.user?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activity.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterType === 'all' || activity.type === filterType;
    
    return matchesSearch && matchesFilter;
  });

  const totalPages = Math.ceil(filteredActivities.length / itemsPerPage);
  const paginatedActivities = filteredActivities.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleRefresh = () => {
    fetchActivities();
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilterType('all');
    setDateRange({ start: '', end: '' });
    setCurrentPage(1);
  };

  if (loading && activities.length === 0) {
    return (
      <div className="activities-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Chargement des activités...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="activities-page">
      <div className="page-header">
        <div className="header-title">
          <h1><FontAwesomeIcon icon={faHistory} /> Journal d'Activités</h1>
          <p>Suivi de toutes les actions sur la plateforme</p>
        </div>
        <button className="btn-refresh" onClick={handleRefresh} disabled={loading}>
          <FontAwesomeIcon icon={faSync} /> Actualiser
        </button>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <FontAwesomeIcon icon={faSearch} />
          <input
            type="text"
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <FontAwesomeIcon icon={faFilter} />
          <select 
            value={filterType} 
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="all">Tous les types</option>
            <option value="login">Connexions</option>
            <option value="create_admin">Création admin</option>
            <option value="delete_admin">Suppression admin</option>
            <option value="update_permissions">Modifications permissions</option>
            <option value="promote_super_admin">Promotions</option>
            <option value="change_password">Changements mot de passe</option>
          </select>
        </div>

        <div className="date-filters">
          <FontAwesomeIcon icon={faCalendar} />
          <input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
          />
          <span>à</span>
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
          />
        </div>

        {(searchTerm || filterType !== 'all' || dateRange.start || dateRange.end) && (
          <button className="btn-clear" onClick={handleClearFilters}>
            Effacer filtres
          </button>
        )}
      </div>

      {/* Stats Summary */}
      <div className="activities-stats">
        <div className="stat-card">
          <span className="stat-value">{filteredActivities.length}</span>
          <span className="stat-label">Total activités</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">
            {activities.filter(a => a.type === 'login').length}
          </span>
          <span className="stat-label">Connexions</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">
            {activities.filter(a => a.type?.includes('create')).length}
          </span>
          <span className="stat-label">Créations</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">
            {activities.filter(a => a.type?.includes('update') || a.type?.includes('permissions')).length}
          </span>
          <span className="stat-label">Modifications</span>
        </div>
      </div>

      {/* Activities List */}
      <div className="activities-list">
        {paginatedActivities.length === 0 ? (
          <div className="no-activities">
            <FontAwesomeIcon icon={faHistory} />
            <p>Aucune activité trouvée</p>
          </div>
        ) : (
          paginatedActivities.map((activity) => {
            const activityInfo = getActivityInfo(activity.type);
            return (
              <div key={activity.id} className="activity-item">
                <div 
                  className="activity-icon"
                  style={{ backgroundColor: `${activityInfo.color}20`, color: activityInfo.color }}
                >
                  <FontAwesomeIcon icon={activityInfo.icon} />
                </div>
                <div className="activity-content">
                  <div className="activity-header">
                    <span className="activity-type" style={{ color: activityInfo.color }}>
                      {activityInfo.label}
                    </span>
                    <span className="activity-time">{formatDate(activity.created_at)}</span>
                  </div>
                  <p className="activity-description">
                    {activity.description || activityInfo.label}
                  </p>
                  <div className="activity-meta">
                    {activity.user && (
                      <span className="meta-item">
                        <FontAwesomeIcon icon={faUser} />
                        {activity.user}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          
          <span className="pagination-info">
            Page {currentPage} sur {totalPages}
          </span>
          
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      )}
    </div>
  );
};

export default Activities;
